import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Download, ArrowLeft, AlertTriangle } from 'lucide-react';
import { Layout } from '../../components/layout/Layout';
import { Button } from '../../components/ui/button';
import { useAuth } from '../../contexts/AuthContext';
import { Panel } from '../../components/programme/insights/HeadlineCards';
import { API, StatGrid, LoginChart, DataTable, fmtDate, pct, downloadXlsx } from '../../components/analytics/AnalyticsBits';
import UserAuditLog from '../../components/analytics/UserAuditLog';
import LoginAsUserButton from '../../components/admin/LoginAsUserButton';

const statsFor = (s) => [
  { key: 'progress', label: 'Avg programme progress', value: `${s.avg_programme_progress}%`, tone: 'text-sky-300' },
  { key: 'logins', label: 'Logins (30 days)', value: s.logins_30d, sub: `${s.logins_total} total` },
  { key: 'last-login', label: 'Last login', value: s.last_login ? fmtDate(s.last_login) : 'Never' },
  { key: 'quiz-attempts', label: 'Quiz attempts', value: s.quiz_attempts, sub: s.quiz_pass_rate !== null ? `${s.quiz_pass_rate}% passed` : null },
  { key: 'quiz-avg', label: 'Average quiz score', value: pct(s.quiz_avg), tone: 'text-amber-300' },
  { key: 'courses', label: 'Courses completed', value: `${s.courses_completed}/${s.courses}` },
  { key: 'labs', label: 'Labs completed · badges', value: `${s.labs_completed} · ${s.badges}` },
  { key: 'time', label: 'Time in courses', value: `${s.time_spent_minutes} min` },
];

export default function LearnerAnalyticsPage() {
  const { userId } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`${API}/admin/analytics/users/${userId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setD(r.data)).catch((e) => setError(e.response?.data?.detail || 'Could not load analytics'));
  }, [userId, token]);

  const name = d?.user.full_name || d?.user.email || 'Learner';
  return (
    <Layout pageTitle={`${name} — Analytics`} breadcrumbItems={[{ label: 'Admin', href: '/admin?tab=users' }, { label: 'Users', href: '/admin?tab=users' }]}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-6" data-testid="learner-analytics-page">
        <button onClick={() => navigate(-1)} className="text-sm text-zinc-400 hover:text-white flex items-center gap-1" data-testid="analytics-back-btn"><ArrowLeft className="h-4 w-4" />Back</button>
        {error && <p className="text-rose-300" data-testid="analytics-error">{error}</p>}
        {!d && !error && <div className="h-64 rounded-2xl bg-zinc-900 animate-pulse" />}
        {d && (<>
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <h1 className="font-heading text-3xl sm:text-4xl font-bold text-white" data-testid="analytics-user-name">{name}</h1>
              <p className="text-zinc-400 text-sm mt-1">{d.user.email} · {d.user.role} · Cohort: <span className="text-white" data-testid="analytics-user-cohort">{d.user.cohort_name || 'None'}</span> · Joined {fmtDate(d.user.created_at)}</p>
              {d.summary.at_risk && <p className="mt-2 inline-flex items-center gap-1 text-xs text-rose-300 bg-rose-500/10 px-2 py-1 rounded-full" data-testid="analytics-at-risk"><AlertTriangle className="h-3 w-3" />Falling behind</p>}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
            <LoginAsUserButton target={d.user} variant="button" />
            <Button onClick={() => downloadXlsx(`${API}/admin/analytics/users/${userId}/export`, token, `${name}_analytics.xlsx`)} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="export-user-analytics-btn">
              <Download className="h-4 w-4 mr-2" />Export to Excel
            </Button>
            </div>
          </div>
          <StatGrid stats={statsFor(d.summary)} />
          <LoginChart data={d.logins_daily} />
          <Panel title="Programmes" testId="analytics-programmes">
            <DataTable testId="analytics-programmes-table" empty="Not enrolled in any programme." rows={d.programmes.map((p) => ({ ...p, key: p.programme_id }))} columns={[
              { label: 'Programme', render: (r) => <span className="text-white">{r.name}</span> },
              { label: 'Progress', render: (r) => `${r.progress}%` },
              { label: 'Activities', render: (r) => `${r.completed_activities}/${r.total_activities}` },
              { label: 'Status', render: (r) => r.status },
              { label: 'Last activity', render: (r) => fmtDate(r.last_activity_at) },
              { label: 'Enrolled', render: (r) => fmtDate(r.enrolled_at) },
            ]} />
          </Panel>
          <Panel title="Quiz & assessment attempts" subtitle="Every attempt, newest first" testId="analytics-quizzes">
            <DataTable testId="analytics-quiz-table" empty="No quiz attempts yet." rows={d.quiz_attempts} columns={[
              { label: 'Date', render: (r) => fmtDate(r.at, true) },
              { label: 'Type', render: (r) => r.source },
              { label: 'Title', render: (r) => <span className="text-white">{r.title}</span> },
              { label: 'Score', render: (r) => pct(r.score) },
              { label: 'Result', render: (r) => (r.passed ? <span className="text-emerald-300">Passed</span> : <span className="text-rose-300">Not passed</span>) },
            ]} />
          </Panel>
          <div className="grid lg:grid-cols-2 gap-6">
            <Panel title="Courses" testId="analytics-courses">
              <DataTable testId="analytics-courses-table" empty="No course enrolments." rows={d.courses} columns={[
                { label: 'Course', render: (r) => <span className="text-white">{r.title}</span> },
                { label: 'Progress', render: (r) => `${r.progress}%` },
                { label: 'Quiz', render: (r) => pct(r.quiz_score) },
                { label: 'Status', render: (r) => r.status },
              ]} />
            </Panel>
            <Panel title="Recent logins" testId="analytics-logins">
              <DataTable testId="analytics-logins-table" empty="No logins recorded yet." rows={d.recent_logins} columns={[
                { label: 'When', render: (r) => fmtDate(r.at, true) },
                { label: 'Method', render: (r) => r.method || 'password' },
              ]} />
            </Panel>
          </div>
          <Panel title="Labs & badges" testId="analytics-labs">
            <DataTable testId="analytics-labs-table" empty="No lab activity yet." rows={[...d.badges.map((b) => ({ ...b, kind: 'Badge', when: b.earned_at || b.created_at, score: b.score })), ...d.labs.map((a) => ({ ...a, kind: a.status === 'completed' ? 'Lab completed' : 'Lab started', when: a.completed_at || a.started_at, score: a.competency_score }))]} columns={[
              { label: 'Type', render: (r) => r.kind },
              { label: 'Lab', render: (r) => <span className="text-white">{r.title}</span> },
              { label: 'Score', render: (r) => pct(r.score) },
              { label: 'When', render: (r) => fmtDate(r.when) },
            ]} />
          </Panel>
          <UserAuditLog userId={userId} token={token} name={name} />
        </>)}
      </div>
    </Layout>
  );
}
